import { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fileToDataUrl } from "@/lib/files";
import { useUsers, useClients, useProjects } from "@/lib/hooks";
import { PRIORITY, TASK_STATUS } from "@/lib/constants";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { UserAvatar } from "@/components/UserAvatar";
import { CollaborationPanel } from "@/components/CollaborationPanel";
import { Check, CheckCircle2, Paperclip, Plus, Trash2 } from "lucide-react";

const empty = {
  title: "",
  description: "",
  assigneeIds: [],
  clientId: "",
  projectId: "",
  priority: "MEDIA",
  dueDate: "",
  status: "A_FAZER",
  subtasks: [],
  dependencyIds: [],
  recurrence: "NONE",
  tags: [],
  estimatedHours: 0,
  attachments: [],
};

export function TaskModal({ open, onOpenChange, task, fixedClientId, fixedProjectId, onSaved }) {
  const qc = useQueryClient();
  const { user: me } = useAuth();
  const { data: users = [] } = useUsers();
  const { data: clients = [] } = useClients();
  const { data: projects = [] } = useProjects();
  const { data: allTasks = [] } = useQuery({
    queryKey: ["tasks", "dependencies"],
    queryFn: async () => (await api.get("/tasks")).data,
    enabled: open,
  });
  const [form, setForm] = useState(empty);
  const [tagsText, setTagsText] = useState("");
  const [newSubtask, setNewSubtask] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      // Uma tarefa nova começa atribuída a quem a cria; pode trocar-se, mas não
      // ficar sem ninguém.
      const next = task
        ? { ...empty, ...task }
        : { ...empty, clientId: fixedClientId || "", projectId: fixedProjectId || "", assigneeIds: me?.id ? [me.id] : [] };
      setForm(next);
      setTagsText((next.tags || []).join(", "));
      setNewSubtask("");
    }
  }, [open, task, fixedClientId, fixedProjectId, me?.id]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleAssignee = (id) =>
    set("assigneeIds", form.assigneeIds.includes(id) ? form.assigneeIds.filter((x) => x !== id) : [...form.assigneeIds, id]);
  const addSubtask = () => {
    if (!newSubtask.trim()) return;
    set("subtasks", [...form.subtasks, { id: crypto.randomUUID(), title: newSubtask.trim(), completed: false }]);
    setNewSubtask("");
  };
  const addAttachment = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const dataUrl = await fileToDataUrl(file);
    set("attachments", [...form.attachments, { id: crypto.randomUUID(), name: file.name, format: file.type, dataUrl }]);
    event.target.value = "";
  };

  const save = async () => {
    if (!form.title.trim()) return toast.error("Indique um título");
    if (form.assigneeIds.length === 0) return toast.error("Escolha pelo menos um responsável");
    setSaving(true);
    try {
      const payload = {
        ...form,
        clientId: form.clientId === "none" ? "" : form.clientId,
        estimatedHours: Number(form.estimatedHours || 0),
        tags: tagsText.split(",").map((tag) => tag.trim()).filter(Boolean),
      };
      if (task?.id) await api.put(`/tasks/${task.id}`, payload);
      else await api.post("/tasks", payload);
      qc.invalidateQueries({ queryKey: ["tasks"] });
      toast.success(task?.id ? "Tarefa atualizada" : "Tarefa criada");
      onOpenChange(false);
      onSaved && onSaved();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="task-modal">
        <DialogHeader>
          <DialogTitle className="font-poppins">{task?.id ? "Editar Tarefa" : "Nova Tarefa"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label className="text-sm">Título</Label>
            <Input value={form.title} onChange={(e) => set("title", e.target.value)} className="mt-1" data-testid="task-title" placeholder="O que precisa de ser feito?" />
          </div>
          <div>
            <Label className="text-sm">Descrição</Label>
            <Textarea value={form.description} onChange={(e) => set("description", e.target.value)} className="mt-1" rows={3} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-sm">Prioridade</Label>
              <Select value={form.priority} onValueChange={(v) => set("priority", v)}>
                <SelectTrigger className="mt-1" data-testid="task-priority"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(PRIORITY).map(([k, v]) => (
                    <SelectItem key={k} value={k}>{v.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-sm">Estado</Label>
              <Select value={form.status} onValueChange={(v) => set("status", v)}>
                <SelectTrigger className="mt-1" data-testid="task-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(TASK_STATUS).map(([k, v]) => (
                    <SelectItem key={k} value={k}>{v.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-sm">Repetição</Label>
              <Select value={form.recurrence} onValueChange={(value) => set("recurrence", value)}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="NONE">Não se repete</SelectItem><SelectItem value="DAILY">Diariamente</SelectItem><SelectItem value="WEEKLY">Semanalmente</SelectItem><SelectItem value="MONTHLY">Mensalmente</SelectItem></SelectContent>
              </Select>
            </div>
            <div><Label className="text-sm">Horas estimadas</Label><Input type="number" min="0" step="0.5" value={form.estimatedHours} onChange={(event) => set("estimatedHours", event.target.value)} className="mt-1" /></div>
          </div>
          <div>
            <Label className="text-sm">Projeto ou campanha</Label>
            <Select value={form.projectId || "none"} onValueChange={(v) => set("projectId", v === "none" ? "" : v)} disabled={Boolean(fixedProjectId)}>
              <SelectTrigger className="mt-1"><SelectValue placeholder="Sem projeto" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Sem projeto</SelectItem>
                {projects.filter((project) => !form.clientId || form.clientId === "none" || project.clientId === form.clientId).map((project) => (
                  <SelectItem key={project.id} value={project.id}>{project.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-sm">Prazo</Label>
              <Input type="date" value={form.dueDate || ""} onChange={(e) => set("dueDate", e.target.value)} className="mt-1" data-testid="task-duedate" />
            </div>
            {!fixedClientId && (
              <div>
                <Label className="text-sm">Cliente</Label>
                <Select value={form.clientId || "none"} onValueChange={(v) => set("clientId", v)}>
                  <SelectTrigger className="mt-1"><SelectValue placeholder="Sem cliente" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Sem cliente</SelectItem>
                    {clients.map((c) => (
                      <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          <div>
            <Label className="text-sm">Responsáveis <span className="text-destructive">*</span></Label>
            {form.assigneeIds.length === 0 && (
              <p className="mt-0.5 text-xs text-destructive" data-testid="task-assignee-required">Escolha pelo menos uma pessoa.</p>
            )}
            <div className="mt-1.5 flex flex-wrap gap-2">
              {users.map((u) => {
                const active = form.assigneeIds.includes(u.id);
                return (
                  <button
                    key={u.id}
                    type="button"
                    onClick={() => toggleAssignee(u.id)}
                    className={`flex items-center gap-1.5 pl-1 pr-2.5 py-1 rounded-full border text-xs transition-colors ${
                      active ? "border-primary bg-primary/10 text-foreground" : "border-border text-muted-foreground hover:border-primary/50"
                    }`}
                    data-testid={`assignee-${u.id}`}
                  >
                    <UserAvatar user={u} size="h-5 w-5" />
                    {u.name.split(" ")[0]}
                    {active && <Check className="h-3 w-3 text-primary" />}
                  </button>
                );
              })}
            </div>
          </div>
          <div>
            <Label className="text-sm">Subtarefas</Label>
            <div className="mt-1.5 space-y-2">
              {form.subtasks.map((subtask) => (
                <div key={subtask.id} className="flex items-center gap-2 rounded-lg border border-border px-3 py-2">
                  <button type="button" onClick={() => set("subtasks", form.subtasks.map((item) => item.id === subtask.id ? { ...item, completed: !item.completed } : item))} className={subtask.completed ? "text-emerald-500" : "text-muted-foreground"}><CheckCircle2 className="h-4 w-4" /></button>
                  <span className={`flex-1 text-sm ${subtask.completed ? "text-muted-foreground line-through" : ""}`}>{subtask.title}</span>
                  <button type="button" onClick={() => set("subtasks", form.subtasks.filter((item) => item.id !== subtask.id))} className="text-muted-foreground hover:text-destructive"><Trash2 className="h-3.5 w-3.5" /></button>
                </div>
              ))}
              <div className="flex gap-2"><Input value={newSubtask} onChange={(event) => setNewSubtask(event.target.value)} onKeyDown={(event) => event.key === "Enter" && (event.preventDefault(), addSubtask())} placeholder="Adicionar subtarefa" /><Button type="button" variant="outline" onClick={addSubtask}><Plus className="h-4 w-4" /></Button></div>
            </div>
          </div>
          <div><Label className="text-sm">Etiquetas</Label><Input value={tagsText} onChange={(event) => setTagsText(event.target.value)} className="mt-1" placeholder="conteúdo, urgente, aprovação" /><p className="mt-1 text-xs text-muted-foreground">Separa por vírgulas.</p></div>
          {task?.id && (
            <div>
              <Label className="text-sm">Depende de</Label>
              <div className="mt-1.5 max-h-32 space-y-1 overflow-y-auto rounded-lg border border-border p-2">
                {allTasks.filter((item) => item.id !== task.id && item.status !== "CONCLUIDO").map((item) => {
                  const selected = form.dependencyIds.includes(item.id);
                  return <button key={item.id} type="button" onClick={() => set("dependencyIds", selected ? form.dependencyIds.filter((id) => id !== item.id) : [...form.dependencyIds, item.id])} className={`flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-xs ${selected ? "bg-primary/10 text-primary" : "hover:bg-muted"}`}><span className={`h-3.5 w-3.5 rounded border ${selected ? "border-primary bg-primary" : "border-border"}`} />{item.title}</button>;
                })}
              </div>
            </div>
          )}
          <div>
            <Label className="text-sm">Anexos</Label>
            <div className="mt-1.5 space-y-2">{form.attachments.map((file) => <div key={file.id} className="flex items-center gap-2 rounded-lg border border-border px-3 py-2"><Paperclip className="h-4 w-4 text-primary" /><span className="flex-1 truncate text-xs">{file.name}</span><button type="button" onClick={() => set("attachments", form.attachments.filter((item) => item.id !== file.id))} className="text-muted-foreground hover:text-destructive"><Trash2 className="h-3.5 w-3.5" /></button></div>)}<label className="inline-flex cursor-pointer items-center gap-1.5 text-xs font-medium text-primary hover:underline"><Paperclip className="h-3.5 w-3.5" /> Anexar ficheiro<input type="file" className="hidden" onChange={addAttachment} /></label></div>
          </div>
          {task?.id && <CollaborationPanel entityType="TASK" entityId={task.id} task={form} />}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button onClick={save} disabled={saving} className="bg-primary text-black hover:bg-primary/90" data-testid="task-save">
            {saving ? "A guardar..." : "Guardar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
